/*
 * Copyright (c) 2020
 * Company: G-Portal
 * Filename: actions.ts
 * Class: actions.ts
 * Author: Karl Magerl<karl@g-portal.com>
 * Version:
 */

import { ActionTree } from 'vuex';
import { AxiosError, AxiosResponse } from 'axios';
import {
  Game, AddonExtension, GameState,
  GameWorkerTaskTypes, ActiveAddonExtension,
} from './types';
import { RootState } from '../types';
import { ApiEndpointEnum, getApiRequestConfig } from '@a/config/network';
import { GetModResponse, Mod, ModTag } from '@c/landingpage/modpack/types';
import { buildServerUrlPath } from './helpers';
import { Route } from 'vue-router';

const MAX_POPULAR_MODS = 20;
const POPULAR_MODS_TAG = 'popular';

const findTagByKey = (tags: Array<ModTag>, needle: string) => (
  tags.find((tag) => tag.slug === needle)
);

/**
 * returns a map with the min rank of a tag
 * @param tags
 * @param mods
 */
const getModToMinimumTagRankMap = (tags: Array<ModTag>, mods: Array<Mod>) : Map<string, number> => {
  const map = new Map<string, number>();

  mods.forEach((mod) => {
    const rankList = mod.tags.map((modTag) => (
      findTagByKey(tags, modTag).rank
    ));

    const list = rankList.length ? rankList : [Number.MAX_VALUE];
    map.set(mod.key, Math.min(...list));
  });

  return map;
};

/**
 * we want to have n popular mods, so we need to mutate the data to have them (if necessary)
 * @param needle
 * @param needed
 * @param mods
 */
const fillupModsByNeedle = (needle: string, needed: number, mods: Array<Mod>) : Array<Mod> => {
  const modsWithNeedle = () => mods.filter((m) => m.tags.includes(needle));
  // --- only when we dont have enough mods we want to enrich them
  if (modsWithNeedle().length < needed) {
    mods
      .sort((a, b) => (a.score > b.score ? -1 : 1))
      .filter((m) => !m.tags.includes(needle))
      .forEach((mod) => {
        if (modsWithNeedle().length < needed && !mod.tags.includes(needle)) {
          mod.tags.push(needle);
        }
      });
  }

  return mods;
};

/**
 * Sort mod by loweset tag rank ASC, mod rank ASC, mod score DESC.
 * @param tags the mod tags
 * @param mods mods
 *
 * @returns mods sorted
 */
const sortMods = (tags: Array<ModTag>, mods: Array<Mod>) : Array<Mod> => {
  const minTagRankMap : Map<string, number> = getModToMinimumTagRankMap(tags, mods);

  const sortedMods = mods.sort((a, b) => {
    if (minTagRankMap.get(a.key) === minTagRankMap.get(b.key)) {
      if (a.rank === b.rank) {
        return a.score > b.score ? -1 : 1;
      }
      return a.rank < b.rank ? -1 : 1;
    }

    return minTagRankMap.get(a.key) < minTagRankMap.get(b.key) ? -1 : 1;
  });

  return sortedMods;
};

export const actions: ActionTree<GameState, RootState> = {
  fetchGame({ commit }, payload: { gameId: number }): Promise<Game> {
    commit('gameLoading');

    return new Promise((resolve, reject) => {
      this.state.$http(`game/${payload.gameId}`, getApiRequestConfig(ApiEndpointEnum.Game))
        .then((response: AxiosResponse<Game>) => {
          const game: Game = response && response.data;
          commit('gameLoaded', game);
          resolve(game);
        }, (error) => {
          commit('gameError');
          reject(error);
        });
    });
  },
  fetchMods(
    { commit, rootState },
  ): Promise<GetModResponse> {
    commit('modsLoading');

    return new Promise((resolve, reject) => {
      rootState.$http.get('minecraft/mods', getApiRequestConfig(ApiEndpointEnum.InterfaceAPI))
        .then((response: AxiosResponse<GetModResponse>) => {
          const res = response && response.data;

          commit('modsLoaded', {
            mods: fillupModsByNeedle(
              POPULAR_MODS_TAG,
              MAX_POPULAR_MODS,
              sortMods(res.tags, res.mods),
            ),
            tags: res.tags,
          });
          resolve(res);
        }, (error) => {
          commit('modsError');
          reject(error);
        });
    });
  },
  initWorker(
    { commit, state },
  ): void {
    if (state.worker != null) {
      state.worker.terminate();
    }
    commit('createWorker');
  },
  terminateWorker(
    { commit, state },
  ): void {
    if (state != null) {
      state.worker.terminate();
    }
    commit('resetWorker');
  },
  groupModsByTags(
    { commit, state },
  ): Promise<GetModResponse> {
    return new Promise((resolve) => {
      state.worker.postMessage({ task: GameWorkerTaskTypes.GROUP_MODS, input: state.mods.data });

      state.worker.onmessage = (event) => {
        commit('modsGrouped', event.data);
        resolve(event.data);
      };
    });
  },
  /**
   * get the information from the backend if the modspace extension is already linked to the server
   * @param commit
   * @param rootState
   * @param payload
   */
  getActivatedModspaceAddon: async (
    { commit, rootState },
    routeValue: Route,
  ) : Promise<void> => {
    commit('activeModspaceAddonLoading');

    return rootState.$http.get(`${buildServerUrlPath(routeValue)}/getActivatedModsSpaceAddon`)
      .then((res: AxiosResponse<ActiveAddonExtension>) => {
        commit('activeModspaceAddonLoaded', res.data);
      }, (error: AxiosError) => {
        // user does not have any purchased modspace addon
        if (error.response.status === 404) {
          commit('activeModspaceAddonLoaded', null);
        } else {
          commit('activeModspaceAddonFailure');
        }
      })
      .catch(() => commit('activeModspaceAddonFailure'));
  },
  /**
   * returns the available extensions for a set route
   * @param commit
   * @param rootState
   * @param payload
   */
  getAvailableExtensions: async (
    { commit, rootState },
    routeValue: Route,
  ) : Promise<void> => {
    commit('availableModspaceAddonsLoading');

    return rootState.$http
      .get(`${buildServerUrlPath(routeValue)}/availableModsSpaceAddons`)
      .then(
        (response: AxiosResponse<Array<AddonExtension>>) => {
          commit('availableModspaceAddonsLoaded', response && response.data);
        }, (error: AxiosError) => {
          if (error.response.status === 404) {
            commit('availableModspaceAddonsLoaded', null);
          } else {
            commit('availableModspaceAddonsFailure');
          }
        },
      )
      .catch(() => commit('availableModspaceAddonsFailure'));
  },
};

